import { type ReactNode } from 'react'
import styles from './JobProgressionUploadModal.module.css'
import {
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetContent,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { useDropzone } from '@/hooks/dropzone-hook'
import { FileButton } from '@/components/FileButton'

export interface JobProgressionUploadModalProps {
  showing: boolean
  dragging: boolean
  title: ReactNode
  description: ReactNode
  isUploadDisabled: boolean
  isUploadPending: boolean
  isSkipDisabled?: boolean
  /**
   * One entry per file being created.
   */
  children?: ReactNode
  onRequestClose: () => unknown
  onAddFiles: (files: FileList) => void
  onDragEnter: () => void
  onDragLeave: () => void
  /**
   * Offers skipping the upload when set.
   */
  onSkip?: () => unknown
  onUpload: () => unknown
  fileInputTestId?: string
  uploadButtonTestId?: string
}

/**
 * Uploads the documents a progression step asks for.
 */
export function JobProgressionUploadModal({
  showing,
  dragging,
  title,
  description,
  isUploadDisabled,
  isUploadPending,
  isSkipDisabled,
  children,
  onRequestClose,
  onAddFiles,
  onDragEnter,
  onDragLeave,
  onSkip,
  onUpload,
  fileInputTestId,
  uploadButtonTestId,
}: JobProgressionUploadModalProps) {
  const { dropzoneProps } = useDropzone<HTMLDivElement>({
    onFilesDropped: onAddFiles,
    onEnter: onDragEnter,
    onLeave: onDragLeave,
  })

  return (
    <ModalSheet
      dropzoneProps={dropzoneProps}
      showing={showing}
      disableCloseOnOutsideClick={true}
      onRequestClose={isUploadPending ? undefined : onRequestClose}
    >
      <ModalSheetHeaderShell size="auto" bordered>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>
                <span>{title}</span>
              </div>
              <div className={styles.headerDescription}>
                <span>{description}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.closeBtn}>
          <ModalSheetHeaderCloseButton />
        </div>
      </ModalSheetHeaderShell>
      <ModalSheetContent>
        <div className={styles.creationItemList}>
          {children}
          <div
            className={
              dragging ? styles.dropZoneItemDragging : styles.dropZoneItem
            }
          >
            <span className={styles.dropZoneTitle}>
              Drag and drop files here or&nbsp;
              <FileButton
                multiple
                onChange={onAddFiles}
                data-test={fileInputTestId}
              >
                <Button
                  label="Upload another file"
                  className={styles.dropZoneButton}
                  borderless
                  variant="text"
                />
              </FileButton>
            </span>
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <div className={styles.footer}>
          <div className={styles.uploadButton}>
            <ModalSheetFooterButtonGroup>
              {onSkip && (
                <Button
                  label="Skip"
                  variant="secondary"
                  isDisabled={isSkipDisabled || isUploadPending}
                  onPress={onSkip}
                />
              )}
              <Button
                label="Upload Documents"
                variant="primary"
                testId={uploadButtonTestId}
                isPending={isUploadPending}
                isDisabled={isUploadDisabled}
                onPress={onUpload}
              />
            </ModalSheetFooterButtonGroup>
          </div>
        </div>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
